import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { apiGet, apiPost } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// API contract (backend/routers/validation.py)
// ---------------------------------------------------------------------------

interface StageOneStatus {
  heading?: string;
  verdict: string | null;
  numerator: number | null;
  denominator: number | null;
  fraction: number | null;
}

interface StageTwoStatus {
  heading?: string;
  verdict: string | null;
  judge_known_yes: number | null;
  judge_known_no: number | null;
  judge_unknown: number | null;
  judge_recall_accuracy: number | null;
}

interface SpendSummary {
  cumulative_usd: number;
  today_usd: number;
  pilot_cap_usd: number;
  day_cap_usd: number;
  events: number;
}

interface EnrollmentSummary {
  enrolled: number;
  forecast: number;
  lapsed: number;
  error_terminal: number;
  pending: number;
}

interface DailyFileEntry {
  file: string;
  sha256: string | null;
  incomplete: boolean;
  stopped: boolean;
}

interface StageThreeStatus {
  heading?: string;
  verdict: string | null;
  spend: SpendSummary;
  enrollment: EnrollmentSummary;
  daily_files: DailyFileEntry[];
  /** True when the API could not read the Redis runtime ledger mirrors —
   * spend/progress then reflect only the committed files and can be stale
   * after any web-triggered run (API/worker split on ECS). */
  mirror_unavailable?: boolean;
}

interface ProtocolResponse {
  protocol: string;
  stages: {
    stage1: StageOneStatus;
    stage2: StageTwoStatus;
    stage3: StageThreeStatus;
    placebo: { verdict: string | null };
  };
  artifacts: Record<string, string | null>;
  web_triggers: {
    available_tests: string[];
    paid_tests: string[];
    paid_triggers_enabled: boolean;
    paid_trigger_flag: string;
    fire_lock_held: boolean | null;
  };
  generated_at: string;
}

interface StageResultsResponse {
  stage: string;
  file: string;
  sha256: string;
  columns: string[];
  rows: Record<string, string>[];
  total: number;
  limit: number;
  offset: number;
}

interface RunResponse {
  success: boolean;
  job_id?: string;
  deduplicated?: boolean;
  test: string;
  paid?: boolean;
}

interface JobStatusResponse {
  job_id: string;
  function: string | null;
  status: string;
  success: boolean | null;
  summary: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Stage verdict chips
// ---------------------------------------------------------------------------

const CHIP_STYLES: Record<string, string> = {
  danger: "bg-red-500/20 text-red-400 border-red-500/40",
  warning: "bg-orange-500/20 text-orange-400 border-orange-500/40",
  neutral: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  active: "bg-green-500/20 text-green-400 border-green-500/40",
};

function VerdictChip({ label, verdict, tone }: { label: string; verdict: string; tone: keyof typeof CHIP_STYLES }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${CHIP_STYLES[tone]}`}>
      <span className="opacity-70">{label}</span>
      {verdict}
    </span>
  );
}

const STAGE_OPTIONS = [
  { key: "stage3", label: "Stage 3 — Forward Test (daily forecasts)" },
  { key: "stage1", label: "Stage 1 — Void Check" },
  { key: "stage2", label: "Stage 2 — Recall Probe" },
  { key: "placebo", label: "Date-Shift Placebo" },
  { key: "stage3_dryrun", label: "Stage 3 — Enrollment Dry-run" },
] as const;

const PAGE_SIZE = 25;

const TERMINAL_JOB_STATUSES = new Set(["complete", "failed", "not_found", "expired"]);

function formatStageOneRate(stage1?: StageOneStatus) {
  const numerator = stage1?.numerator;
  const denominator = stage1?.denominator;
  if (numerator == null || denominator == null || denominator <= 0) {
    return "--";
  }
  const fraction = stage1?.fraction ?? numerator / denominator;
  return `${numerator}/${denominator} (${(fraction * 100).toFixed(1)}%)`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ValidationPage() {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<string>("stage3");
  const [offset, setOffset] = useState(0);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const protocolQuery = useQuery({
    queryKey: ["validation", "protocol"],
    queryFn: () => apiGet<ProtocolResponse>("/api/validation/protocol"),
    refetchInterval: 30_000,
  });

  const resultsQuery = useQuery({
    queryKey: ["validation", "results", stage, offset],
    queryFn: () =>
      apiGet<StageResultsResponse>(
        `/api/validation/results/${stage}?limit=${PAGE_SIZE}&offset=${offset}`,
      ),
  });

  const runMutation = useMutation({
    mutationFn: (test: string) => apiPost<RunResponse>("/api/validation/run", { test }),
    onSuccess: (data) => {
      if (data.job_id) {
        setActiveJobId(data.job_id);
        // The backend reuses a STABLE job id when the same test is re-run
        // after completing (it clears ARQ's old result first). If the id
        // matches the current one, the setter above is a no-op and the job
        // query would stay cached in its old terminal state with polling
        // disabled — invalidate it so the rerun is refetched and the 3s
        // polling resumes (Codex P2, 2026-06-12).
        void queryClient.invalidateQueries({
          queryKey: ["validation", "job", data.job_id],
        });
      }
      protocolQuery.refetch();
    },
  });

  const jobQuery = useQuery({
    queryKey: ["validation", "job", activeJobId],
    queryFn: () => apiGet<JobStatusResponse>(`/api/validation/run/${activeJobId}`),
    enabled: Boolean(activeJobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_JOB_STATUSES.has(status) ? false : 3_000;
    },
  });

  const protocol = protocolQuery.data;
  const stage1 = protocol?.stages.stage1;
  const stage2 = protocol?.stages.stage2;
  const stage3 = protocol?.stages.stage3;
  const placebo = protocol?.stages.placebo;
  const paidEnabled = protocol?.web_triggers.paid_triggers_enabled ?? false;
  const fireLockHeld = protocol?.web_triggers.fire_lock_held ?? false;

  const results = resultsQuery.data;
  const job = jobQuery.data;
  const jobRunning = Boolean(activeJobId) && !(job && TERMINAL_JOB_STATUSES.has(job.status));

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Protocol status header */}
      <Panel
        title="Validation Protocol v2.1"
        description="Pre-registered two-arm forward test — evidence parsed from the committed, hash-locked files"
      >
        {protocolQuery.isError ? (
          <div className="text-sm text-red-400">Can&apos;t reach the validation backend.</div>
        ) : !protocol ? (
          <div className="text-sm text-slate-400">Loading protocol status...</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <VerdictChip label="Stage 1" verdict={stage1?.verdict ?? "—"} tone="danger" />
              <VerdictChip
                label="Stage 2"
                verdict={stage2?.verdict ?? "—"}
                tone="warning"
              />
              <VerdictChip label="Placebo" verdict={placebo?.verdict ?? "—"} tone="neutral" />
              <VerdictChip label="Stage 3" verdict="FORWARD TEST RUNNING" tone="active" />
              {stage3?.mirror_unavailable && (
                <span title="The API could not read the Redis runtime ledger mirrors — spend and progress below reflect only the committed baseline files and may be missing recent web-triggered runs.">
                  <VerdictChip
                    label="Ledgers"
                    verdict="MIRROR OFFLINE — counts may be stale"
                    tone="warning"
                  />
                </span>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard
                label="Stage 1 mispriced/non-triggering"
                value={formatStageOneRate(stage1)}
                tone="danger"
              />
              <StatCard
                label="Stage 2 JUDGE knows outcome"
                value={
                  stage2?.judge_known_yes != null && stage2?.judge_known_no != null
                    ? `${stage2.judge_known_yes + stage2.judge_known_no}/74 markets`
                    : "--"
                }
                hint={
                  stage2?.judge_recall_accuracy != null
                    ? `${(stage2.judge_recall_accuracy * 100).toFixed(1)}% recall accuracy`
                    : undefined
                }
                tone="warning"
              />
              <StatCard
                label="Pilot spend"
                value={
                  stage3
                    ? `$${stage3.spend.cumulative_usd.toFixed(2)} / $${stage3.spend.pilot_cap_usd.toFixed(0)}`
                    : "--"
                }
                hint={stage3 ? `today $${stage3.spend.today_usd.toFixed(2)} / $${stage3.spend.day_cap_usd.toFixed(0)}` : undefined}
              />
              <StatCard
                label="Forward-test progress"
                value={stage3 ? `${stage3.enrollment.forecast} forecast / ${stage3.enrollment.enrolled} enrolled` : "--"}
                hint={stage3 ? `${stage3.enrollment.pending} pending · ${stage3.enrollment.lapsed} lapsed` : undefined}
                tone="success"
              />
            </div>
          </>
        )}
      </Panel>

      {/* Run triggers */}
      <Panel
        title="Run Tests"
        description="Free scans run for any signed-in user. The paid daily fire stays dark until Hans enables it after the pilot window."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              onClick={() => runMutation.mutate("stage3_dryrun")}
              disabled={runMutation.isPending || jobRunning}
            >
              {runMutation.isPending ? "Queueing..." : "Run enrollment dry-run ($0)"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => runMutation.mutate("stage3_enroll")}
              disabled={runMutation.isPending || jobRunning}
            >
              Enroll new markets ($0)
            </Button>
            <span
              title={
                paidEnabled
                  ? fireLockHeld
                    ? "A fire run is already in progress"
                    : "Fires today's two-arm paid forecasts under the $20/day + $50 pilot caps"
                  : "Enabled after the pilot window (VALIDATION_WEB_PAID_TRIGGERS is off)"
              }
            >
              <Button
                variant="destructive"
                onClick={() => runMutation.mutate("stage3_daily_fire")}
                disabled={!paidEnabled || fireLockHeld || runMutation.isPending || jobRunning}
              >
                Fire daily run (paid)
              </Button>
            </span>
          </div>
        }
      >
        <div className="text-xs text-slate-400">
          Admin-only paid trigger; hard stops are the runner&apos;s frozen rails ($20/day, $50 pilot). A
          Redis lock prevents overlapping fires. Every run&apos;s CSV SHA-256 is shown below for tamper
          evidence.
        </div>
        {runMutation.isError && (
          <div className="mt-2 text-sm text-red-400">
            {runMutation.error instanceof Error ? runMutation.error.message : "Trigger failed."}
          </div>
        )}
        {activeJobId && (
          <div className="mt-3 rounded-lg bg-gunmetal/50 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-xs text-slate-400">job {activeJobId}</span>
              <span
                className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                  job?.status === "complete"
                    ? "bg-green-500/20 text-green-400"
                    : job?.status === "failed"
                      ? "bg-red-500/20 text-red-400"
                      : "bg-blue-500/20 text-blue-400"
                }`}
              >
                {job?.status ?? "queued"}
              </span>
              {jobRunning && <span className="text-xs text-slate-500">polling every 3s...</span>}
            </div>
            {job?.summary && (
              <pre className="mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap break-all text-xs text-slate-300">
                {JSON.stringify(job.summary, null, 2)}
              </pre>
            )}
          </div>
        )}
      </Panel>

      {/* Stage results */}
      <Panel
        title="Stage Results"
        description={
          results
            ? `${results.file} — sha256 ${results.sha256.slice(0, 16)}…`
            : "Committed evidence CSVs, paginated"
        }
        actions={
          <Select
            value={stage}
            onChange={(e) => {
              setStage(e.target.value);
              setOffset(0);
            }}
            title="Select validation stage"
          >
            {STAGE_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </Select>
        }
      >
        {resultsQuery.isError ? (
          <div className="text-sm text-slate-400">No results file for this stage yet.</div>
        ) : !results ? (
          <div className="text-sm text-slate-400">Loading results...</div>
        ) : (
          <>
            <div className="max-h-[420px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {results.columns.map((col) => (
                      <TableHead key={col}>{col}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.rows.map((row, i) => (
                    <TableRow key={i}>
                      {results.columns.map((col) => (
                        <TableCell key={col} className="max-w-[280px] truncate font-mono text-xs">
                          {row[col]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
              >
                Prev
              </Button>
              <span>
                {results.total === 0
                  ? "0 rows"
                  : `rows ${offset + 1}–${Math.min(offset + PAGE_SIZE, results.total)} of ${results.total}`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= results.total}
              >
                Next
              </Button>
              <span className="ml-auto font-mono">full sha256: {results.sha256}</span>
            </div>
          </>
        )}
      </Panel>

      {/* Daily evidence chain */}
      {stage3 && stage3.daily_files.length > 0 && (
        <Panel title="Daily Forecast Files" description="Tamper-evident chain — one CSV per day, never edited (PREREGISTRATION §6)">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>SHA-256</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stage3.daily_files.map((f) => (
                <TableRow key={f.file}>
                  <TableCell className="font-mono text-xs">{f.file}</TableCell>
                  <TableCell className="max-w-[360px] truncate font-mono text-xs text-slate-400">
                    {f.sha256 ?? "--"}
                  </TableCell>
                  <TableCell>
                    {f.incomplete && (
                      <span className="rounded bg-yellow-500/20 px-1.5 py-0.5 text-xs text-yellow-400">
                        INCOMPLETE
                      </span>
                    )}
                    {f.stopped && (
                      <span className="ml-1 rounded bg-red-500/20 px-1.5 py-0.5 text-xs text-red-400">
                        STOPPED
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
    </div>
  );
}
